import { useCallback, useEffect, useState } from "react";
import { Save } from "lucide-react";

import {
  ObservabilityService,
  type UsdIdrRateGetResponse,
  type UsdIdrRateUpdatePayload,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import {
  formatNumberID,
  formatRelativeTimeID,
} from "@/lib/format";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Skeleton } from "@/components/ui/skeleton";
import { Section } from "@/components/shared/section";

const TENANT_ADMIN_ROLES = new Set(["admin", "super_admin"]);

const PLATFORM_DEFAULT_RATE = 15500;
const MIN_RATE = 1000;
const MAX_RATE = 100000;

function isValidRate(input: string): boolean {
  const n = Number(input.replace(",", "."));
  if (!Number.isFinite(n)) return false;
  return n >= MIN_RATE && n <= MAX_RATE;
}

export default function SettingsFxRateTab() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const canEdit = user ? TENANT_ADMIN_ROLES.has(user.role) : false;

  const fetcher = useCallback(() => ObservabilityService.getFxRate(), []);
  const { data, error, isLoading, refetch } =
    useApi<UsdIdrRateGetResponse>(fetcher);

  const [rate, setRate] = useState("");
  const [reason, setReason] = useState("");
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (data?.rate !== undefined) {
      setRate(String(Math.round(data.rate)));
    }
  }, [data?.rate]);

  const update = useMutation<UsdIdrRateUpdatePayload, unknown>(
    (body) => ObservabilityService.updateFxRate(body),
    {
      onSuccess: () => {
        setSavedAt(new Date());
        setReason("");
        void refetch();
      },
    }
  );

  const currentRate = data?.rate;
  const isPlatformDefault =
    currentRate !== undefined &&
    Math.round(currentRate) === PLATFORM_DEFAULT_RATE;

  const rateValid = isValidRate(rate);
  const parsedRate = rateValid ? Number(rate.replace(",", ".")) : null;
  const dirty =
    parsedRate !== null &&
    currentRate !== undefined &&
    parsedRate !== currentRate;
  const canSubmit = canEdit && dirty && rateValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || parsedRate === null) return;
    const payload: UsdIdrRateUpdatePayload = { rate: parsedRate };
    const trimmedReason = reason.trim();
    if (trimmedReason) payload.reason = trimmedReason;
    await update.mutate(payload);
  };

  const handleReset = () => {
    if (currentRate === undefined) return;
    setRate(String(Math.round(currentRate)));
    setReason("");
    update.reset();
  };

  if (isLoading && !data) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error && !data) {
    return <Alert variant="warning">{parseApiError(error)}</Alert>;
  }

  return (
    <div className="space-y-8">
      {update.error && (
        <Alert variant="danger">{parseApiError(update.error)}</Alert>
      )}
      {savedAt && !update.error && (
        <Alert variant="success" role="status">
          {t("settings.fxRate.savedNotice", {
            time: formatRelativeTimeID(savedAt),
          })}
        </Alert>
      )}
      {!canEdit && (
        <Alert variant="info">{t("settings.fxRate.readOnlyNotice")}</Alert>
      )}
      <Alert variant="info">
        {t("settings.fxRate.governanceCaption")}
      </Alert>

      <Section
        title={t("settings.fxRate.section.title")}
        description={t("settings.fxRate.section.description")}
      >
        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-lg border border-ocean-100 bg-white p-5"
        >
          <div className="flex flex-col gap-1">
            <div className="label-eyebrow">
              {t("settings.fxRate.section.currentLabel")}
            </div>
            <div className="flex items-center gap-2 text-lg font-medium text-ocean-900">
              Rp {currentRate !== undefined ? formatNumberID(Math.round(currentRate)) : "—"}
              <span className="text-sm font-normal text-neutral-600">/ USD</span>
              {isPlatformDefault ? (
                <Badge tone="neutral">
                  {t("settings.fxRate.section.platformDefault")}
                </Badge>
              ) : (
                <Badge tone="success">
                  {t("settings.fxRate.section.tenantCustom")}
                </Badge>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
            <FormField
              label={t("settings.fxRate.section.inputLabel")}
              type="number"
              value={rate}
              onChange={setRate}
              disabled={!canEdit}
              min={MIN_RATE}
              max={MAX_RATE}
              step="0.01"
              error={
                rate && !rateValid
                  ? t("settings.fxRate.section.inputError")
                  : undefined
              }
              hint={t("settings.fxRate.section.inputHint")}
            />
            <FormField
              label={t("settings.fxRate.section.reasonLabel")}
              value={reason}
              onChange={setReason}
              maxLength={200}
              disabled={!canEdit}
              hint={t("settings.fxRate.section.reasonHint")}
              placeholder={t("settings.fxRate.section.reasonPlaceholder")}
            />
          </div>

          {canEdit && (
            <div className="flex items-center justify-end gap-2 border-t border-ocean-100 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={update.isLoading || !dirty}
              >
                {t("settings.fxRate.section.resetButton")}
              </Button>
              <Button type="submit" disabled={update.isLoading || !canSubmit}>
                <Save size={14} />
                {update.isLoading
                  ? t("settings.fxRate.section.saveButtonLoading")
                  : t("settings.fxRate.section.saveButton")}
              </Button>
            </div>
          )}
        </form>
      </Section>
    </div>
  );
}
